import { useMsal } from '@azure/msal-react'
import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { logoutIntercom } from 'src/services/intercom'

import LogoIcon from '../../assets/icons/logo.svg'
import MenuIcon from '../../assets/icons/menu.svg'
import WarnIcon from '../../assets/svgIcons/WarnIcon'
import { UserProfile } from '../../types'

interface HeaderProps {
  toggleSidebar: () => void
  userData?: UserProfile
}

export function Header({ toggleSidebar, userData }: HeaderProps) {
  const navigate = useNavigate()
  const { instance } = useMsal()
  const initial = userData?.firstName ? userData.firstName.charAt(0).toUpperCase() : ''
  const fullName = userData?.firstName
    ? `${userData.firstName}${userData.lastName.charAt(0).toUpperCase() ? ' ' + userData.lastName.charAt(0).toUpperCase() : ''}`.toUpperCase()
    : ''

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNavigate = (path: string) => {
    setDropdownOpen(false)
    navigate(path)
  }

  const handleLogout = () => {
    const sessionKeys = Object.keys(localStorage)
    sessionKeys.forEach(key => {
      if (key.includes('msal')) {
        localStorage.removeItem(key)
      }
    })
    logoutIntercom()
    localStorage.removeItem('auth_code')
    localStorage.removeItem('auth_state')
    localStorage.removeItem('auth_timestamp')
    localStorage.removeItem('accessToken')
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    })
  }

  return (
    <header className="main-header">
      <div className="header-container">
        <div className="container-item">
          <div className="flex pl-1 items-center gap-6">
            <button onClick={toggleSidebar}>
              <img src={MenuIcon} alt="menu" height="20" width="20" />
            </button>
            <button onClick={() => navigate('/')}>
              <img src={LogoIcon} alt="logo" height="24" width="163" />
            </button>
          </div>

          <div className="flex items-center gap-8">
            {/* Avatar + Dropdown wrapper */}
            <div className="profile-dropdown-wrapper" ref={dropdownRef}>
              {userData?.avatarUrl ? (
                <img
                  src={userData.avatarUrl}
                  alt=""
                  className="profile-icon"
                  onClick={() => setDropdownOpen(prev => !prev)}
                  aria-haspopup="true"
                  aria-expanded={dropdownOpen}
                />
              ) : (
                <button
                  className="profile-icon"
                  onClick={() => setDropdownOpen(prev => !prev)}
                  aria-haspopup="true"
                  aria-expanded={dropdownOpen}
                >
                  {initial}
                </button>
              )}
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="profile-dropdown">
                  {/* User section */}
                  <div className="dropdown-section">
                    <span className="dropdown-section-label">{fullName}</span>
                    <button
                      className="flex items-center justify-between dropdown-item"
                      onClick={() => handleNavigate('/profile')}
                    >
                      <span>Profile</span>
                      {/* Warning icon if profile incomplete */}
                      {(!userData?.firstName || !userData?.lastName) && (
                        <span className="flex items-center">
                          <WarnIcon />
                        </span>
                      )}
                    </button>
                  </div>

                  <div className="dropdown-divider" />

                  {/* Logout */}
                  <div className="dropdown-section">
                    <button className="dropdown-item" onClick={handleLogout}>
                      Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
